import './root.css';
import fs from 'node:fs/promises';
import path from 'node:path';
import { fileURLToPath } from 'node:url';
import type { Props as FooterProps } from '@micro-server/ni-footer/settings';
import type { Props as HeaderProps } from '@micro-server/ni-header/settings';
import { captureException } from '@sentry/react-router';
import { clsx } from 'clsx';
import {
  COUNTRY_COOKIE,
  COUNTRY_HEADER,
  DEFAULT_LOCALE,
} from 'dotcom-core/src/constants';
import {
  getISOLocale,
  getUrlLocale,
  isValidUrlLanguage,
  replaceLanguageInUrl,
  supportedLanguages,
} from 'dotcom-core/src/helpers/locale';
import { useEffect, useRef, useState } from 'react';
import {
  Links,
  type LinksFunction,
  type LoaderFunctionArgs,
  Meta,
  type MetaFunction,
  Outlet,
  Scripts,
  ScrollRestoration,
  data,
  isRouteErrorResponse,
  useLoaderData,
  useLocation,
  useNavigate,
  useRouteError,
} from 'react-router';
import { ROUTE_SEGMENTS } from '~/helpers/app-routes';
import type { Route } from './+types/root';
import { BannerDealSOS } from './components/BannerDealSOS';
import {
  EnglishGeneralErrorPage,
  GeneralErrorPage,
} from './components/GeneralErrorPage';
import { Micro } from './components/Micro';
import env from './env';
import type { Language } from './graphql';
import {
  type GraphqlServerClient,
  getMicroFrontendData,
  makeClient,
} from './helpers/client';
import { getCookie } from './helpers/cookie';
import { mapGraphQLError } from './helpers/error';
import { ExperimentsScripts } from './helpers/experiments/ExperimentsScript';
import { flattenMessages } from './helpers/intl';

import { TypedEventBus } from 'dotcom-core/src/components/EventBus';
import { isTruthy } from './helpers/predicate';

export const mobileModalRootId = 'mobile-modal-root';

function App() {
  const data = useLoaderData<RootLoaderData>();

  if ('error' in data) throw new Error(data.error);

  // @TODO: Remove this if not used, if we always use bg-white?

  // const { pathname } = useLocation();
  // const isAccountPage = useMatch(ROUTE_PATHS.account);
  // const isCartPage = useMatch(ROUTE_PATHS.cart);
  // const isCheckoutPage = [...Object.values(ROUTE_PATHS)].some((checkoutPath) =>
  //   matchPath(checkoutPath, pathname),
  // );

  useEffect(() => {
    TypedEventBus.emit('ni-v2/hydrated');
  }, []);

  return (
    <main>
      <PageHeader />
      <div
        className={clsx(
          'flex flex-col min-h-screen',
          'bg-white',
          // isAccountPage || isCartPage || isCheckoutPage
          //   ? 'bg-grey-10'
          //   : 'bg-white',
        )}
      >
        <div className="flex-auto">
          <Outlet />
        </div>
      </div>
      <Micro id="ni-footer" html={data.footerHtml} />
    </main>
  );
}

function Layout({ children }: { children: React.ReactNode }) {
  const data = useLoaderData<RootLoaderData>();

  if (!data || (data && 'error' in data)) {
    return (
      <html lang="en">
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <Meta />
          <Links />
        </head>
        <body>
          {children}
          <Scripts />
        </body>
      </html>
    );
  }

  return (
    <html lang={data.language}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <ExperimentsScripts />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <script
          dangerouslySetInnerHTML={{
            __html: `
            window.__ENVIRONMENT__ = ${JSON.stringify(data.ENV ?? {})};
            window['__BUILD_META__'] = typeof window['__BUILD_META__'] === 'object' ? window['__BUILD_META__'] : {};
            window['__BUILD_META__']['ni-v2'] = ${data.buildMeta};
        `,
          }}
        />
        <Scripts />
      </body>
    </html>
  );
}

function PageHeader() {
  const html = useLoaderData().headerHtml;
  const microHeaderRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const { hydration } = useMicroFrontendsHydration();

  useEffect(() => {
    if (hydration['ni-header'] === false) return;

    const microHeaderEl = microHeaderRef.current;

    if (!microHeaderEl) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target;

      if (!(target instanceof HTMLElement)) return;

      const isHeaderNavItem = !!target.getAttribute('data-ni-header-nav');
      const href = target.getAttribute('href');

      if (!(isHeaderNavItem && href)) return;

      event.preventDefault();
      event.stopPropagation();
      navigate({
        pathname: href.replace(window.location.origin, ''),
        search: window.location.search,
      });
    };

    microHeaderEl.addEventListener('click', handleClick);

    return () => {
      microHeaderEl.removeEventListener('click', handleClick);
    };
  }, [navigate, hydration['ni-header']]);

  // god forgive me for this double effect
  useEffect(() => {
    if (hydration['ni-header'] === false) return;

    const microHeaderEl = microHeaderRef.current;

    if (!microHeaderEl || typeof window === 'undefined') return;

    microHeaderEl.querySelectorAll('[data-ni-header-nav]').forEach((item) => {
      const href = item
        .getAttribute('href')
        ?.replace(window.location.origin, '');

      if (href && location.pathname.startsWith(href)) {
        item.setAttribute('aria-current', 'page');
      } else {
        item.removeAttribute('aria-current');
      }
    });
  }, [location, hydration['ni-header']]);

  return (
    <>
      <BannerDealSOS />

      <div id={mobileModalRootId} className="sticky top-0 z-50">
        <Micro id="ni-header" html={html ?? ''} ref={microHeaderRef} />
      </div>
    </>
  );
}

function ErrorBoundary() {
  const data = useLoaderData();
  const error = useRouteError();
  const isDevelopment = import.meta.env.MODE === 'development';

  if (isDevelopment) {
    if (isRouteErrorResponse(error)) {
      return (
        <div>
          <h1 className="webex-h1">
            {error.status} {error.statusText}
          </h1>
          <p>{error.data}</p>
        </div>
      );
    }
    if (error instanceof Error) {
      return (
        <div>
          <h1 className="webex-h1">Unexpected Error</h1>
          <p>{error.message}</p>
        </div>
      );
    }
  }

  if (isRouteErrorResponse(error)) {
    if (error.status !== 404) captureException(error);
    // fallback for errors before root loader runs (no loader data available).
    return <EnglishGeneralErrorPage />;
  }

  if (error && error instanceof Error) {
    captureException(error);
  }

  return (
    <div>
      {data?.headerHtml && <PageHeader />}
      <GeneralErrorPage />
      {data?.footerHtml ? (
        <Micro id="ni-footer" html={data.footerHtml} />
      ) : null}
    </div>
  );
}

const __dirname = path.dirname(fileURLToPath(import.meta.url));
const resolve = (p: string) => path.resolve(__dirname, p);

async function loader({ params, request }: Route.LoaderArgs) {
  try {
    const { country, language } = getIntl({ params, request });
    const buildMeta = await getBuildMeta();
    const client = makeClient({ params, request });
    const messages = await getTranslations({ language, client });
    const { headerHtml, footerHtml } = await getMicroFrontends({
      country,
      language,
      url: request.url,
    });

    return data(
      {
        ENV: {
          NODE_ENV: env.NODE_ENV,
          DEPLOYMENT_ENVIRONMENT: env.DEPLOYMENT_ENVIRONMENT,
          APP_BASE_URL: env.APP_BASE_URL,
          APP_TAG_MANAGER_PROFILE: env.APP_TAG_MANAGER_PROFILE,
          APP_ADYEN_CLIENT_KEY: env.APP_ADYEN_CLIENT_KEY,
          APP_SENTRY_AUTH_TOKEN: env.APP_SENTRY_AUTH_TOKEN,
          APP_SENTRY_DSN: env.APP_SENTRY_DSN,
          ALGOLIA_APP_ID: env.ALGOLIA_APP_ID,
          ALGOLIA_API_KEY: env.ALGOLIA_API_KEY,
          ALGOLIA_ENV_SUFFIX: env.ALGOLIA_ENV_SUFFIX,
          KAMELEOON_SITE_CODE: env.KAMELEOON_SITE_CODE,
          LOG_LEVEL: env.LOG_LEVEL,
        } satisfies Partial<typeof env>,
        buildMeta,
        language,
        country,
        messages,
        headerHtml,
        footerHtml,
      },
      { headers: { 'Cache-Control': 'public, max-age=86400' } },
    );
  } catch (error) {
    const errorMessage = mapGraphQLError({
      error,
      operationName: 'Root loader',
      extra: { params },
    });

    return data(
      {
        error: errorMessage,
      },
      {
        status: 500,
        headers: { 'Cache-Control': 'no-store' },
      },
    );
  }
}

// root data is stable so we don't need revalidation
export const shouldRevalidate = () => false;

const links: LinksFunction = () => [
  {
    rel: 'shortcut icon',
    href: '/assets/favicon.ico',
  },
  {
    rel: 'kameleoon iframe',
    href: '/assets/kameleoon_static_file.html',
  },
];

const meta: MetaFunction = ({ location }) => {
  const url = `${env.APP_BASE_URL}${location.pathname}`;
  return [
    { name: 'robots', content: 'index, follow' },
    {
      tagName: 'link',
      rel: 'canonical',
      href: url,
    },
    ...supportedLanguages.map((lang) => {
      return {
        tagName: 'link',
        rel: 'alternate',
        hrefLang: lang,
        href: replaceLanguageInUrl(url, getUrlLocale(lang)),
      };
    }),
    {
      tagName: 'link',
      rel: 'alternate',
      hrefLang: 'x-default',
      href: replaceLanguageInUrl(url, DEFAULT_LOCALE),
    },
  ];
};

async function getTranslations({
  language,
  client,
}: { language: Language; client: GraphqlServerClient }) {
  const { translations } = await client.Translations({
    language: getISOLocale(language),
  });
  const messages = flattenMessages(translations) ?? {};

  if (!isTruthy(Object.keys(messages).length)) {
    throw new Error('Failed to fetch translations.');
  }

  return messages;
}

export function getIntl({
  request,
  params,
}: {
  params: LoaderFunctionArgs['params'];
  request: LoaderFunctionArgs['request'];
}) {
  const cookieHeader = request.headers.get('Cookie');
  const language = params.language as Language; // always enforced by route
  const country =
    getCookie(cookieHeader, COUNTRY_COOKIE) ||
    request.headers.get(COUNTRY_HEADER);

  if (!country) {
    throw new Error(
      `Invalid country. Please provide a ${COUNTRY_COOKIE} cookie with a valid country code (e.g. "DE", "FR").`,
    );
  }

  if (!language || !isValidUrlLanguage(language)) {
    throw new Error(
      `Invalid language: ${language}. Please provide a valid language before the page path (e.g. "/en/page-path", "/de/page-path")`,
    );
  }

  return { country, language };
}

async function getBuildMeta() {
  let buildMeta = '"unavailable"';

  try {
    buildMeta = await fs.readFile(resolve('../build.json'), 'utf-8');
  } catch {
    // fail silently as this is not critical data
  }

  return buildMeta;
}

async function getMicroFrontends({
  country,
  language,
  url,
}: {
  country: string;
  language: Language;
  url: Request['url'];
}) {
  const [headerHtml = '', footerHtml = ''] = await Promise.all([
    getMicroFrontendData<HeaderProps>({
      id: 'ni-header',
      language: getISOLocale(language),
      country,
      catalogLinks:
        url.includes(ROUTE_SEGMENTS.catalog) ||
        url.includes(ROUTE_SEGMENTS.pricing),
    }),
    getMicroFrontendData<FooterProps>({
      id: 'ni-footer',
      language: getISOLocale(language),
      withBanner:
        url.includes(ROUTE_SEGMENTS.pricing) ||
        url.includes(ROUTE_SEGMENTS.catalog),
      type: 'reduced',
      country,
    }),
  ]);

  return { headerHtml, footerHtml };
}

type RootLoaderData = typeof loader;

const headers = ({ loaderHeaders }: Route.HeadersArgs) => {
  return {
    'Cache-Control': loaderHeaders.get('Cache-Control'),
  };
};

function useMicroFrontendsHydration() {
  const [hydration, setHydration] = useState({
    'ni-header': false,
    'ni-footer': false,
  });

  useEffect(() => {
    function hydrateMicroFrontend(
      name: 'ni-footer' | 'ni-header',
      retriesLeft = 5,
      delay = 500,
    ) {
      if (hydration[name]) return;

      const hydrate =
        window[name === 'ni-header' ? 'hydrateNiHeader' : 'hydrateNiFooter'];

      if (typeof hydrate === 'function') {
        hydrate();
        setHydration((prev) => ({ ...prev, [name]: true }));
      } else {
        // biome-ignore lint/suspicious/noConsole: We want this for now
        console.error(
          `Could not hydrate "${name}" because its hydration function is not yet available. Header/footer functionality may be affected.`,
        );

        if (retriesLeft > 0) {
          setTimeout(() => {
            hydrateMicroFrontend(name, retriesLeft - 1, delay);
          }, delay);
        }
      }
    }

    hydrateMicroFrontend('ni-header');
    hydrateMicroFrontend('ni-footer');

    return TypedEventBus.listen('micro-server/client-ready', ({ detail }) => {
      if (detail.name === 'ni-footer' || detail.name === 'ni-header') {
        hydrateMicroFrontend(detail.name);
      }
    });
  }, [hydration]);

  return { hydration };
}

export {
  headers,
  loader,
  links,
  Layout,
  meta,
  ErrorBoundary,
  type RootLoaderData,
};

export default App;
